import React from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputProps,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { ff, navCtaOffset } from '../../theme';

/** Condensed uppercase page title (45px on tab screens, smaller for names). */
export const DisplayHeading: React.FC<{ children: React.ReactNode; size?: string; color?: string; as?: React.ElementType }> = ({
  children,
  size = '30px',
  color = ff.ink,
  as = 'h1',
}) => (
  <Text as={as} textStyle="display" fontSize={size} color={color} m={0}>
    {children}
  </Text>
);

/** Condensed money number with a tiny uppercase label; greyed when the value is zero. */
export const MoneyFigure: React.FC<{
  value: React.ReactNode;
  label: string;
  size?: string;
  dim?: boolean;
  color?: string;
}> = ({ value, label, size = '26px', dim = false, color = ff.ink }) => (
  <HStack spacing={1} align="baseline" color={dim ? ff.faint : color}>
    <Text textStyle="display" fontSize={size} textTransform="none">
      {value}
    </Text>
    <Text textStyle="eyebrow" fontSize="10px" letterSpacing="0.08em">
      {label}
    </Text>
  </HStack>
);

/** Pill search field. */
export const PillSearch: React.FC<InputProps> = props => (
  <HStack
    as="label"
    fontFamily="display"
    h="48px"
    px={4}
    spacing="10px"
    borderRadius="full"
    border="1px solid"
    borderColor={ff.border}
    bg="white"
    color={ff.text2}
    _focusWithin={{ borderColor: ff.ink }}
  >
    <FiSearch size={18} aria-hidden />
    <Input
      variant="unstyled"
      fontSize="16px"
      color={ff.ink}
      _placeholder={{ color: ff.muted }}
      {...props}
    />
  </HStack>
);

export type UnderlineTab = { key: string; label: string; badge?: number | string };

/** Underline tabs with optional ink/lime count badges. */
export const UnderlineTabs: React.FC<{
  tabs: UnderlineTab[];
  active: string;
  onChange: (key: string) => void;
  gap?: string;
  h?: string;
}> = ({ tabs, active, onChange, gap = '22px', h = '44px' }) => (
  <Flex
    role="tablist"
    fontFamily="display"
    gap={gap}
    overflowX="auto"
    borderBottom="1px solid"
    borderColor={ff.line}
    sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
  >
    {tabs.map(t => {
      const on = t.key === active;
      return (
        <Box
          as="button"
          role="tab"
          aria-selected={on}
          key={t.key}
          onClick={() => onChange(t.key)}
          h={h}
          mb="-1px"
          flex="none"
          display="flex"
          alignItems="center"
          gap="6px"
          fontSize="15px"
          fontWeight={on ? 800 : 500}
          color={on ? ff.ink : ff.text2}
          borderBottom="3px solid"
          borderColor={on ? ff.ink : 'transparent'}
          whiteSpace="nowrap"
        >
          {t.label}
          {t.badge ? (
            <Flex
              as="span"
              minW="20px"
              h="20px"
              px="6px"
              borderRadius="full"
              bg={ff.ink}
              color={ff.lime}
              align="center"
              justify="center"
              fontSize="11px"
              fontWeight={600}
            >
              {t.badge}
            </Flex>
          ) : null}
        </Box>
      );
    })}
  </Flex>
);

/** Status / meta pill. */
export const Pill: React.FC<{ children: React.ReactNode; tone?: 'ink' | 'lime' | 'grey' | 'outline' }> = ({
  children,
  tone = 'grey',
}) => {
  const tones = {
    ink: { bg: ff.ink, color: ff.lime },
    lime: { bg: ff.lime, color: ff.ink },
    grey: { bg: ff.grey, color: ff.ink },
    outline: { bg: 'white', color: ff.ink, boxShadow: `inset 0 0 0 1px ${ff.border}` },
  };
  return (
    <Flex
      as="span"
      fontFamily="display"
      h="26px"
      px="10px"
      borderRadius="full"
      align="center"
      fontSize="12px"
      fontWeight={600}
      whiteSpace="nowrap"
      flex="none"
      {...tones[tone]}
    >
      {children}
    </Flex>
  );
};

/** Copy plus primary and optional secondary action. */
export const EmptyState: React.FC<{
  title: string;
  body?: string;
  primary?: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
}> = ({ title, body, primary, secondary }) => (
  <VStack spacing={3} py={10} px={2} textAlign="center" fontFamily="display">
    <Text textStyle="display" fontSize="30px">
      {title}
    </Text>
    {body && (
      <Text fontSize="15px" color={ff.text2} maxW="300px">
        {body}
      </Text>
    )}
    {primary && (
      <Button variant="ink" h="48px" px={6} mt={2} onClick={primary.onClick}>
        {primary.label}
      </Button>
    )}
    {secondary && (
      <Button variant="link" color={ff.ink} fontWeight={600} textDecoration="underline" onClick={secondary.onClick}>
        {secondary.label}
      </Button>
    )}
  </VStack>
);

/** Fixed bottom CTA bar; sits above the floating nav unless `noNav`. */
export const StickyCtaBar: React.FC<{ children: React.ReactNode; noNav?: boolean }> = ({ children, noNav }) => (
  <Box
    position="fixed"
    fontFamily="display"
    left={0}
    right={0}
    bottom={noNav ? 0 : navCtaOffset}
    zIndex={20}
    px={5}
    pt={3}
    pb={noNav ? 'max(16px, env(safe-area-inset-bottom))' : 3}
    bg="white"
    borderTop="1px solid"
    borderColor={ff.line}
  >
    <Box maxW="560px" mx="auto">
      {children}
    </Box>
  </Box>
);
